/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: settings.ts
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  26.09.22 3:20 pm.
 * Last modified: 26.09.22 3:20 pm.
 *
 * More information at: https://egt-digital.com/
 */
import * as config from '../config';
import { LAST_PLAYED_SECTION_NAME, LAST_PLAYED_STORAGE_KEY, MOST_PLAYED_SECTION_NAME } from '../constants';
import store from '../eventStore';
import IGameSettings, { IGameResponse, IGroupGame, INormalizedGameResponse } from '../interfaces/IGameSettings';
import ISectionSettings from '../interfaces/ISectionSettings';
import ISettings from '../interfaces/ISettings';
import { getLocalStorageItem, gridBlocksCount, normalize, pipe, posTypes } from './helpers';

const staticBannerSections = ['TOURNAMENT_INBET'];

const addEGTCurrency = (settingsResponse: any): any[] => {
    settingsResponse.games.forEach((game: any) => {
        const denomKeys = Object.keys(game.denominations).sort();
        if (denomKeys.length > 0) {
            game.denominations.EGT = [...game.denominations[denomKeys[0]]];
        }
        if (game.hasOwnProperty('betOptions')) {
            const keys = Object.keys(game.betOptions);
            game.betOptions = { ...game.betOptions, EGT: [...game.betOptions[keys[0]]] };
        }
        if (game.hasOwnProperty('bets')) {
            const keys = Object.keys(game.bets);
            game.bets = { ...game.bets, EGT: game.bets[keys[0]] };
        }
    });
    return settingsResponse;
};

/**
 * A section may have an expiration date
 * If there is such date and it has passed, don't show the section
 */
const filterExpiredSections = (section: { expiration?: string }) => {
    if (!section.expiration) {
        return true; // There is no expiration date so no need to check it
    } else {
        return new Date() < new Date(section.expiration); // Check if the current date is smaller than the expiration date
    }
};

/**
 * A section may have an introduction date
 * If there is such date and it has passed, show the section
 */
const filterIntroducedSections = (section: { introduction?: string }) => {
    if (!section.introduction) {
        return true; // There is no introduction date so no need to check it
    } else {
        return new Date() > new Date(section.introduction); // Check if the current date is bigger than the introduction date
    }
};

export const parseSettingsResponse = (settingsResponse: any): ISettings => {
    // TEMPORARY  OBJECT, UNTIL WE RECEIVE IT FROM THE SERVER
    // Object.assign(settingsResponse, { restrictions: ['noJackpot'] });
    const newSettings: ISettings = {
        languageCode: store.SETTINGS.value.languageCode,
        units: store.SETTINGS.value.units,
        currency: store.SETTINGS.value.currency,
        supportedLanguages: settingsResponse.languages,
        selectedSection: store.SCROLL_STORAGE.value.selectedSection,
        sections: [],
        playerId: store.SETTINGS.value.playerId,
        gameKey: store.SETTINGS.value.gameKey,
        mode: store.SETTINGS.value.mode,
        host: store.SETTINGS.value.host,
        openType: store.SETTINGS.value.openType,
        apiHost: store.SETTINGS.value.apiHost,
        redirectHost: store.SETTINGS.value.redirectHost,
        isSettingsModalOpen: store.SETTINGS.value.isSettingsModalOpen,
        isSearchOpen: store.SETTINGS.value.isSearchOpen,
        useUpdateResizables: store.SETTINGS.value.useUpdateResizables,
        supportedGames: {},
        restrictions: settingsResponse.restrictions,
    };

    const normalizedGames: INormalizedGameResponse = normalize('gameKey', settingsResponse.games);
    settingsResponse.groups
        .filter((a: { active: boolean }) => a.active)
        .filter(filterIntroducedSections)
        .filter(filterExpiredSections)
        .sort((a: { order: number }, b: { order: number }) => a.order - b.order)
        .forEach((group: { name: string; jackpotName?: string; games: any[]; topLimit?: number }) => {
            const section: ISectionSettings = {
                name: group.name,
                jackpotName: group?.jackpotName || null,
                icon: group.name.toLowerCase() + '_icon',
                banner: staticBannerSections.some(b => b === group.name),
                gridBlocks: group.games.length,
                topLimit: group.topLimit,
                games: group.games
                    .sort((a, b) => a.order - b.order)
                    .map<IGameSettings>((game: IGroupGame) => {
                        const gameKey = game.gameKey;
                        const gameData = normalizedGames[gameKey];
                        return parseGameSettings(game, gameData);
                    }),
            };

            section.gridBlocks = gridBlocksCount(section.games);
            newSettings.sections.push(section);
        });

    // Cache supported games
    newSettings.supportedGames = normalizedGames;

    return newSettings;
};

export const parseGameSettings = (game: IGroupGame, gameData: IGameResponse): IGameSettings => {
    const denominationCurrencies = Object.keys(gameData.denominations);

    const betCurrencies = gameData.hasOwnProperty('bets') ? Object.keys(gameData.bets) : null;
    const maxBet = new Map<string, number>();
    const minPossibleBets = new Map<string, number>();
    const maxPossibleBets = new Map<string, number>();

    const minDenomination = new Map<string, number>();
    const denominations = new Map<string, number[]>();

    denominationCurrencies.forEach(currencyCode => {
        const currencyDenominations: number[] = gameData.denominations[currencyCode].sort(
            (a: number, b: number) => a - b,
        );
        //// maximumBetCalc ////
        let maximumBet = 0;
        try {
            maximumBet = calculateMinMaxBet(
                'maxBet',
                gameData.initialFactor,
                currencyDenominations,
                gameData?.betOptions[currencyCode],
            );
        } catch (err) {
            // TODO: do nothing if missing bet options data
        }

        //// minimumBetCalc ////
        let minimumBet = 0;
        try {
            minimumBet = calculateMinMaxBet(
                'minBet',
                gameData.initialFactor,
                currencyDenominations,
                gameData.betOptions[currencyCode],
            );
        } catch (err) {
            // TODO: do nothing if missing bet options data
        }

        maxPossibleBets.set(currencyCode, maximumBet);
        minPossibleBets.set(currencyCode, minimumBet);
        denominations.set(currencyCode, currencyDenominations);
        minDenomination.set(currencyCode, currencyDenominations[0]);
    });

    betCurrencies?.forEach(currencyCode => {
        maxBet.set(currencyCode, gameData.bets[currencyCode]);
    });
    //// multiplayer and instant games DO NOT have denominations and initialFactor, only betOptions for different currencies ///
    if (gameData.gameType === 'MULTI_GAME' || gameData.gameType === 'INSTANT') {
        if (maxPossibleBets.size) {
            maxPossibleBets.clear();
        }
        if (minPossibleBets.size) {
            minPossibleBets.clear();
        }

        if (gameData.hasOwnProperty('betOptions')) {
            const { betOptions } = gameData;
            const multiplayerGameBetCurrancies = Object.keys(betOptions);

            multiplayerGameBetCurrancies.forEach(currencyCode => {
                const possibleBets = (betOptions[currencyCode] as number[]).sort((a, b) => a - b);
                const minPlayerBet = possibleBets[0] / 100;
                const maxPlayerBet = possibleBets[possibleBets.length - 1] / 100;

                // error if NaN to set zero in order to NOT show min-max bets
                minPossibleBets.set(currencyCode, minPlayerBet ? minPlayerBet : 0);
                maxPossibleBets.set(currencyCode, maxPlayerBet ? maxPlayerBet : 0);
            });
        }
    }
    const gameSetting: IGameSettings = {
        name: gameData.name,
        gameKey: gameData.gameKey,
        jackpotName: gameData.jackpotName || null,
        minDenomination,
        denominations,
        order: game.order,
        banners: {
            lobby: { ...posTypes[game.imageLobby] },
            section: { ...posTypes[game.imageSection] },
        },
        branding: gameData?.branding || null,
        maxBet,
        maxPossibleBets,
        minPossibleBets,
    };
    return gameSetting;
};

const fixMysteryJackpotGroupValue = (data: any): any => {
    data.groups.forEach((group: any) => {
        if (group.name === 'MYSTERY') {
            group.jackpotName = 'Clover Chance';
        }
    });

    return data;
};

const fixProgressiveSectionBanners = (data: any): any => {
    data.groups.forEach((group: any) => {
        if (group.name === 'PROGRESSIVE') {
            group.games.forEach((game: any) => (game.imageLobby = 'vertical'));
        }
    });

    return data;
};

// HINT: This function add the instant games if not returned by the server
// eslint-disable-next-line
const addInstantGames = (responseValue: any): void => {
    const instantLinkGroup: {
        name: any;
        order: any;
        active: any;
        jackpotName: any;
        expiration: any;
        games: any;
    } = {
        name: 'INSTANT',
        order: -1,
        active: true,
        jackpotName: 'InstantLink',
        expiration: null,
        games: [],
    };
    const labels = [
        'Mighty God of the Oceans',
        'Egypt Glory',
        'Happy Clovers',
        'Lucky Seven Hot',
        'Mr. Action Money',
        'Super Clovers',
        "Rico's Treasure",
    ];
    const instantGameKeys = [
        'InstPsd',
        'InstEgypt',
        'InstHClover',
        'InstLucky7Hot',
        'InstMrAction',
        'InstSuperClovers',
        'InstRico',
        // 'InstLOC',
        // 'InstDazzlingSpace',
        // 'InstShinyCrown',
    ];
    responseValue.games = responseValue.games.filter((game: any) => game.gameKey.indexOf('Slot') !== -1);
    responseValue.groups = responseValue.groups.filter((group: any) => group.name.indexOf('Instant') === -1);

    instantGameKeys.forEach((gameKey, idx) => {
        responseValue.games.push({
            gameKey,
            name: labels[idx],
            description: 'QATEST',
            denominations: {},
            jackpotName: 'InstantLink',
        });
        instantLinkGroup.games.push({
            gameKey,
            imageSection: gameKey === 'InstPsd' ? 'vertical' : gameKey === 'InstRico' ? 'horizontal' : 'rectangular',
            imageLobby: gameKey === 'InstPsd' ? 'vertical' : gameKey === 'InstRico' ? 'horizontal' : 'rectangular',
            order: idx + 1,
        });
    });
    responseValue.groups.push(instantLinkGroup);
};

const getAllAvailableCurrencies = (responseValue: any): string[] => {
    const returnValue: string[] = [];

    responseValue.games.forEach((game: any) => {
        const currencies = Object.keys(game.denominations);
        currencies.forEach(currency => {
            if (!returnValue.includes(currency)) {
                returnValue.push(currency);
            }
        });
    });

    return returnValue;
};

const mockInstantGamesDenominations = (responseValue: any): any => {
    const availableCurrencies = getAllAvailableCurrencies(responseValue);

    responseValue.games.forEach((game: any) => {
        if (game.gameKey.indexOf('Inst') !== -1) {
            const denominations = Object.keys(game.denominations);
            availableCurrencies.forEach(currency => {
                if (denominations.indexOf(currency) === -1) {
                    game.denominations[currency] = [1];
                }
            });
        }
    });

    return responseValue;
};

const overwriteLastPlayedGames = (responseValue: any) => {
    const lastPlayedSection = responseValue.groups.find((group: any) => group.name === LAST_PLAYED_SECTION_NAME);
    if (lastPlayedSection) {
        lastPlayedSection.games = [];

        const lastPlayedStorageKey = `${LAST_PLAYED_STORAGE_KEY}_${store.SETTINGS.value.playerId}`;
        const lastPlayedGames = JSON.parse(getLocalStorageItem(lastPlayedStorageKey, store.SETTINGS.value.gameKey));
        responseValue.games.forEach((game: IGameSettings) => {
            if (lastPlayedGames.includes(game.gameKey)) {
                const i = lastPlayedGames.indexOf(game.gameKey);
                lastPlayedGames.splice(i, 1, { ...game });
            }
        });
        // filter games before populate them because of https://jira.egt-digital.com/browse/ON-36734
        lastPlayedSection.games = [...lastPlayedGames.filter((game: any) => typeof game !== 'string')];
    }
    return responseValue;
};
const setVerticalBanners = (responseValue: any) => {
    responseValue.groups.forEach((group: any) =>
        group.games.forEach((game: any) => {
            game.imageLobby = 'vertical';
            game.imageSection = 'vertical';
        }),
    );

    return responseValue;
};

// helper function for dev testing only
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const overwriteMostPlayedGames = (responseValue: any) => {
    const mostPlayedSection = responseValue.groups.find((group: any) => group.name === MOST_PLAYED_SECTION_NAME);

    if (mostPlayedSection) {
        mostPlayedSection.games = [
            {
                gameKey: 'ABSlot',
                order: 1,
                imageLobby: 'vertical',
                imageSection: 'vertical',
            },
            {
                gameKey: 'VNSlot',
                order: 2,
                imageLobby: 'vertical',
                imageSection: 'vertical',
            },
            {
                gameKey: 'DRHCSlot',
                order: 3,
                imageLobby: 'vertical',
                imageSection: 'vertical',
            },
            {
                gameKey: 'PCHCSlot',
                order: 7,
                imageLobby: 'vertical',
                imageSection: 'vertical',
            },
        ];
    }

    return responseValue;
};

export const settings = (): void => {
    const currentSettings = store.SETTINGS.value;
    const request = new XMLHttpRequest();
    request.addEventListener('load', function(): void {
        const responseValue = JSON.parse(this.responseText);
        // HINT: Uncomment the below to add mock instant games data
        // addInstantGames(responseValue);
        pipe(
            addEGTCurrency,
            mockInstantGamesDenominations,
            fixMysteryJackpotGroupValue,
            fixProgressiveSectionBanners,
            overwriteLastPlayedGames,
            setVerticalBanners, //
            // overwriteMostPlayedGames,
        )(responseValue);
        store.COMMUNICATION_CHANNEL.next({
            event: 'settings',
            value: responseValue,
        });
    });
    request.addEventListener('error', (): void => {
        store.EXTERNAL_CHANNEL.next({
            event: 'error',
            value: new Error('LOBBY.FETCH_SETTINGS_FAILED'),
        });
    });
    request.open(
        'GET',
        config.getSettingsUrl(currentSettings.apiHost, currentSettings.currency.toUpperCase() === 'EGT'),
    );
    request.send();
};

export const getSectionScrollPossition = (
    name: string,
): {
    top: number;
    left: number;
    scrollHeight: number;
} => {
    return {
        top: store.SCROLL_STORAGE.value.sections?.[name]?.top || 0,
        left: store.SCROLL_STORAGE.value.sections?.[name]?.left || 0,
        scrollHeight: store.SCROLL_STORAGE.value.scrollHeight,
    };
};

const calculateMinMaxBet = (
    type: 'minBet' | 'maxBet',
    initialFactor: number,
    denominations: number[],
    betOptions: number[],
): number => {
    try {
        if (type === 'minBet') {
            return (initialFactor * denominations[0] * betOptions[0]) / 100;
        }
        return (initialFactor * denominations[denominations.length - 1] * betOptions[betOptions.length - 1]) / 100;
    } catch (err) {
        return 0;
    }
};
